/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Select } from 'antd'
import React from 'react'
import { paragraphMedium, weightRegular } from 'src/components/css'
import { ErrorLabel } from '../ErrorLabel'
import Text from '../Text'

type MultiSelectInputProps = {
  label: React.ReactNode
  disabled?: boolean
  options: Array<{ label: string; value: string }>
  name: string
  value: string[]
  onChange: (value: string[]) => void
  fieldErrors: any
  customError: any
  placeholder?: string
  tabIndex?: number
  backgroundColor?: string
}

export const MultiSelectInput: React.FC<MultiSelectInputProps> = ({
  label,
  disabled = false,
  options,
  name,
  value,
  onChange,
  fieldErrors,
  customError,
  placeholder,
  tabIndex,
  backgroundColor = colors.SECONDARY_200,
}) => {
  const hasError =
    fieldErrors?.[name] ||
    (Array.isArray(fieldErrors) && fieldErrors?.includes(name))

  return (
    <>
      <style>
        {`
          .ant-select-dropdown {
            z-index: 999999 !important;
          }
          .ant-select-multiple .ant-select-selector {
            background: ${backgroundColor} !important;
            border-radius: 8px !important;
          }
          .ant-select-multiple .ant-select-selection-item {
            font-size: 14px !important;
            font-weight: 400 !important;
            color: ${colors.WHITE} !important;
            background: ${colors.SECONDARY_300} !important;
            border-color: ${colors.SECONDARY_400} !important;
          }
          .ant-select-dropdown .ant-select-item-option-content {
            font-size: 14px !important;
            font-weight: 400 !important;
            color: ${colors.WHITE} !important;
          }
        `}
      </style>
      <div css={labelRow}>
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.WHITE}
          style={`
            align-self: stretch;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          `}
        >
          {label}
        </Text>
      </div>
      <Select
        mode='multiple'
        tabIndex={tabIndex}
        placeholder={placeholder}
        allowClear
        disabled={disabled}
        value={value}
        options={options}
        onChange={(v: string[]) => onChange(v)}
        popupMatchSelectWidth={false}
        css={selectStyle(backgroundColor, disabled)}
        status={hasError ? 'error' : ''}
      />
      <ErrorLabel
        fieldErrors={fieldErrors || []}
        customError={customError || ''}
        name={name}
      />
    </>
  )
}

const labelRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  align-self: stretch;
`

const selectStyle = (backgroundColor: string, disabled: boolean) => css`
  ${paragraphMedium}
  ${weightRegular}
  font-size: 14px !important;
  color: ${colors.WHITE};
  width: 100%;
  background: ${backgroundColor} !important;
  border-radius: 8px !important;
  min-height: 40px !important;
  ${disabled && 'opacity: 0.5 !important;'}
`
